import {
  capabilityDefinition,
  errorDefinition,
  errorMessage,
  type CapabilityKey,
  type ScreenState,
  type TranslationKey,
} from "@planner/core";
import type { ReactNode } from "react";
import { useLocale } from "../i18n/provider.js";
import { requestAccessFor } from "./RequestAccess.js";

/**
 * الحالات الأربع — **تُكتب هنا مرة واحدة، وترثها الشاشات الثلاث والأربعون**.
 *
 * والأهم من التوحيد: `children` دالةٌ تستقبل `data`. فالمحتوى **لا يمكن أن
 * يُرسَم** قبل أن تكون الحالة `ready`، لأن `data` لا وجود له قبلها. وهذا يجعل
 * «تحميلٌ فاشل يُعرَض كشاشة فارغة» غير قابل للكتابة أصلاً — لا خطأً نتذكّر
 * ألا نرتكبه. في v2 أصاب هذا العطب ثلاث شاشات، أخطرها مركز الإجراءات: كان خطأ
 * الخادم يُعرَض على أنه «مفيش حاجة محتاجة إجراء».
 */

export type EmptyCopy = {
  /** ماذا يُفترض أن يكون هنا — لا «لا توجد بيانات» */
  readonly titleKey: TranslationKey;
  readonly bodyKey: TranslationKey;
  readonly action?: { readonly labelKey: TranslationKey; readonly onClick: () => void };
};

export type ScreenProps<T> = {
  readonly state: ScreenState<T>;
  readonly children: (data: T) => ReactNode;
  /** بلا هذه لا يمكن للمستخدم أن يحاول ثانيةً — فهي إلزامية */
  readonly onRetry: () => void;
  readonly onRequestAccess?: (capability: CapabilityKey) => void;
  readonly empty?: EmptyCopy;
  /** هيكل التحميل بشكل المحتوى الحقيقي. بلا واحد يُرسَم هيكلٌ عام. */
  readonly skeleton?: ReactNode;
};

function Skeleton(): ReactNode {
  return (
    <div className="sk-wrap" aria-hidden="true">
      <div className="sk" style={{ width: "62%" }} />
      <div className="sk" />
      <div className="sk" style={{ width: "84%" }} />
      <div className="sk" style={{ width: "47%" }} />
    </div>
  );
}

export function Screen<T>({
  state,
  children,
  onRetry,
  onRequestAccess,
  empty,
  skeleton,
}: ScreenProps<T>): ReactNode {
  const { t, locale } = useLocale();

  if (state.status === "loading") {
    return (
      <div className="state" role="status" aria-live="polite" aria-busy="true">
        <span className="sr-only">{t("state.loading.title")}</span>
        {skeleton ?? <Skeleton />}
      </div>
    );
  }

  if (state.status === "error") {
    /**
     * **الكود ورقم الربط ظاهران دائماً** — وهذا هو المطلب المركزي حرفياً:
     * يقرؤهما المستخدم في التليفون فيصلان كما هما، فيُفتح السجلّ عليهما
     * مباشرةً. بانرٌ عام بلا كود هو ما نمنعه بقاعدة lint.
     */
    return (
      <div className="state sbx st-error" role="alert">
        <div className="ic ic-dang" aria-hidden="true">
          <IconAlert />
        </div>
        <h2>{t("state.error.title")}</h2>
        <p>{errorMessage(locale, state.error)}</p>
        {/**
         * **وما يفعله المستخدمُ يُقال له هو** — لا ما نفعله نحن.
         *
         * فكان يُعرَض هنا **علاجُ الكتالوج**، وهو مكتوبٌ لمن يحقّق: «افتح
         * الحادثة برقم المرجع — فيها الطلب كامل ومكان العطل بالضبط». ومن
         * يقرؤه موظّفٌ لا يفتح حوادثَ ولا يملك شاشتَها، **فيقف أمام جملةٍ لا
         * تعنيه**. والعلاجُ يبقى في شاشة كتالوج الأخطاء لمن يفتحها.
         */}
        {/**
         * **و«عندنا» لا تُقال إلا إذا كانت عندنا.** فكانت تُقال على كلّ فشل —
         * «مش موجودة» ثم «حصل خطأ عندنا، مش عندك» **في نفس الشاشة**، جملتان
         * تكذّب إحداهما الأخرى. **فعطبُ السيرفر (٥xx، أو كودٌ لا نعرفه) عندنا**،
         * وما سواه — غيرُ موجود، ليس من صلاحيتك، تعارض — يُقال بلا اتّهامٍ لأحد.
         */}
        <p className="state-cause">
          {t(
            (errorDefinition(state.error.code)?.status ?? 500) >= 500
              ? "state.error.body"
              : "state.error.bodyRequest",
          )}
        </p>
        {/**
         * **والكودُ والمرجعُ باسميهما** — فهما المطلبُ المركزيّ للمنتج كلّه:
         * «كودٌ يقوله الموظّف لشادي». وكانا يُعرَضان رقمين عاريين، **فلا يعرف
         * من يقرؤهما أيَّهما يقول** ولا أنهما يُقالان أصلاً.
         */}
        <dl className="codes">
          <div>
            <dt>{t("state.error.code")}</dt>
            <dd className="code" data-testid="error-code">
              {state.error.code}
            </dd>
          </div>
          <div>
            <dt>{t("state.error.reference")}</dt>
            <dd className="code" data-testid="error-correlation">
              {state.error.correlationId}
            </dd>
          </div>
        </dl>
        <button type="button" className="btn gh" onClick={onRetry}>
          {t("common.retry")}
        </button>
      </div>
    );
  }

  if (state.status === "forbidden") {
    /**
     * ليست رسالة رفض جافّة: الشاشة تسمّي **القدرة الناقصة باسمها البشري**،
     * وتعرض طلبها بضغطة. فمن يُمنَع يعرف ماذا يطلب وممّن.
     */
    const capability = state.capability as CapabilityKey;
    const readable = capabilityDefinition(capability).label[locale];
    return (
      <div className="state sbx st-forbidden" role="alert">
        <div className="ic ic-warn" aria-hidden="true">
          <IconLock />
        </div>
        <h2>{t("state.forbidden.title")}</h2>
        <p>{t("state.forbidden.body")}</p>
        <p className="state-capability">
          <span className="code">{readable}</span>
        </p>
        {/**
         * **والطلبُ من هنا دائماً** — لا من شاشةٍ تتذكّر أن تمرّره.
         *
         * فكان `onRequestAccess` اختيارياً **ولا تمرّره شاشةٌ واحدة**: الزرُّ
         * مكتوبٌ ولا يُرسَم، ولا في النظام كلِّه بابٌ آخر يطلب صلاحية.
         */}
        <button
          type="button"
          className="btn"
          onClick={() => {
            if (onRequestAccess) onRequestAccess(capability);
            else requestAccessFor(capability);
          }}
        >
          {t("state.forbidden.request")}
        </button>
      </div>
    );
  }

  if (state.status === "empty") {
    const copy = empty;
    return (
      <div className="state sbx st-empty">
        <div className="ic ic-accent" aria-hidden="true">
          <IconInbox />
        </div>
        <h2>{t(copy?.titleKey ?? "state.empty.title")}</h2>
        <p>{t(copy?.bodyKey ?? "state.empty.body")}</p>
        {copy?.action ? (
          <button type="button" className="btn" onClick={copy.action.onClick}>
            {t(copy.action.labelKey)}
          </button>
        ) : null}
      </div>
    );
  }

  return <>{children(state.data)}</>;
}

/* ── أيقونات الحالات — داخل الملف لأنها لا تُستعمل خارجه ───────────────── */

function IconAlert(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

function IconLock(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconInbox(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M21 15V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v13l4-4h12a2 2 0 0 0 2-2" />
    </svg>
  );
}
